import React, { useState, useEffect } from 'react';
import { CapacitorPurchases } from '@capgo/capacitor-purchases';
import { Capacitor } from '@capacitor/core';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { subscriptionAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Zap, Users, BarChart2, X } from 'lucide-react';

const REVENUECAT_API_KEY = process.env.REACT_APP_REVENUECAT_API_KEY || '';

const PRO_FEATURES = [
  { icon: Users, text: 'Atleti illimitati' },
  { icon: Zap, text: 'Funzionalità avanzate' },
  { icon: BarChart2, text: 'Report completi' },
];

export default function PaywallModal({ open, onOpenChange, onSuccess }) {
  const { user, updateUser } = useAuth();
  const [monthlyPackage, setMonthlyPackage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingOfferings, setLoadingOfferings] = useState(false);
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => {
    if (!open || !isNative || !REVENUECAT_API_KEY) return;

    const initAndFetch = async () => {
      setLoadingOfferings(true);
      try {
        await CapacitorPurchases.setup({ apiKey: REVENUECAT_API_KEY, appUserID: user?.id });
        const { offerings } = await CapacitorPurchases.getOfferings();
        // Prova prima .monthly, poi il primo package disponibile nell'offering corrente
        const current = offerings?.current;
        const monthly =
          current?.monthly ??
          current?.availablePackages?.[0] ??
          null;
        console.log('[RevenueCat] current offering:', JSON.stringify(current));
        console.log('[RevenueCat] monthly package:', JSON.stringify(monthly));
        setMonthlyPackage(monthly);
      } catch (err) {
        console.error('RevenueCat init error', err);
      } finally {
        setLoadingOfferings(false);
      }
    };

    initAndFetch();
  }, [open, isNative, user?.id]);

  const handlePurchase = async () => {
    if (!isNative) {
      toast.error('Gli acquisti in-app sono disponibili solo sull\'app mobile');
      return;
    }
    if (!monthlyPackage) {
      toast.error('Prodotto non disponibile al momento');
      return;
    }

    setLoading(true);
    try {
      const { customerInfo } = await CapacitorPurchases.purchasePackage({
        identifier: monthlyPackage.identifier,
        offeringIdentifier: monthlyPackage.offeringIdentifier,
      });

      const activeEntitlement = Object.values(customerInfo.entitlements.active)[0];
      const response = await subscriptionAPI.activate({
        product_id: monthlyPackage.product.identifier,
        transaction_id: activeEntitlement?.productIdentifier ?? monthlyPackage.product.identifier,
        expires_at: customerInfo.latestExpirationDate ?? null,
      });

      updateUser(response.data.coach);
      toast.success('Piano Pro attivato!');
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      if (err?.code !== 1) {
        toast.error('Acquisto non riuscito. Riprova.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!isNative) return;
    setLoading(true);
    try {
      const { customerInfo } = await CapacitorPurchases.restorePurchases();
      const hasActive = Object.keys(customerInfo.entitlements.active).length > 0;
      if (hasActive) {
        const activeEntitlement = Object.values(customerInfo.entitlements.active)[0];
        const response = await subscriptionAPI.activate({
          product_id: activeEntitlement.productIdentifier,
          transaction_id: activeEntitlement.productIdentifier,
          expires_at: activeEntitlement.expirationDate ?? null,
        });
        updateUser(response.data.coach);
        toast.success('Acquisti ripristinati!');
        onOpenChange(false);
        onSuccess?.();
      } else {
        toast.info('Nessun acquisto precedente trovato');
      }
    } catch {
      toast.error('Errore nel ripristino degli acquisti');
    } finally {
      setLoading(false);
    }
  };

  const priceLabel = monthlyPackage?.product?.priceString
    ? `${monthlyPackage.product.priceString} / mese`
    : 'Abbonamento mensile';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        className="bg-zinc-900 border border-white/10 rounded-2xl w-[calc(100vw-24px)] max-w-sm"
      >
        <DialogHeader>
          <DialogTitle className="text-white text-2xl font-black text-center">
            Passa a <span className="text-[#ccff00]">Pro</span>
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-center text-sm">
            Hai raggiunto il limite di 10 atleti del piano gratuito
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-2">
          {PRO_FEATURES.map(({ icon: Icon, text }) => (
            <div key={text} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#ccff00]/10 flex items-center justify-center flex-shrink-0">
                <Icon className="w-4 h-4 text-[#ccff00]" />
              </div>
              <span className="text-white text-sm font-medium">{text}</span>
            </div>
          ))}
        </div>

        <div className="space-y-3 mt-2">
          <Button
            onClick={handlePurchase}
            disabled={loading || (isNative && loadingOfferings)}
            className="w-full bg-[#ccff00] text-black hover:bg-[#b3e600] font-black text-base rounded-xl py-6"
          >
            {loading ? 'Elaborazione...' : loadingOfferings ? 'Caricamento...' : priceLabel}
          </Button>

          {isNative && (
            <button
              onClick={handleRestore}
              disabled={loading}
              className="w-full text-zinc-500 text-xs hover:text-zinc-300 transition-colors"
            >
              Ripristina acquisti
            </button>
          )}

          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="w-full text-zinc-500 hover:text-zinc-300 rounded-xl"
          >
            Non ora
          </Button>

          <div className="flex justify-center gap-4 pt-1">
            <a
              href="https://trainersmanager.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 text-xs hover:text-zinc-300 transition-colors underline"
            >
              Privacy Policy
            </a>
            <a
              href="https://trainersmanager.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-zinc-500 text-xs hover:text-zinc-300 transition-colors underline"
            >
              Terms of Use
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
